import React, { useState } from 'react';
import { useOrganization } from '@/hooks/useOrganization';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import contextPacksService, {
  ContextPack,
  ContextPackCreate,
  ContextPackUpdate,
} from '@/lib/api/contextPacks';
import salesAgentService from '@/lib/api/salesAgent';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Plus,
  Pencil,
  Trash2,
  Copy,
  Link,
  Loader2,
  PackageOpen,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const DEMO_BASE_URL = (
  import.meta.env.VITE_DEMO_BASE_URL || 'https://demo.hihobbes.com'
).replace(/\/$/, '');

function buildDemoUrl(demoSlug: string | null | undefined, packId: string): string {
  if (!demoSlug) return `${DEMO_BASE_URL}/?cp=${packId}`;
  return `${DEMO_BASE_URL}/${demoSlug}/${packId}`;
}

// ── Inline editor form ─────────────────────────────────────────────────────

interface PackFormData {
  name: string;
  context_text: string;
  prefill_name: string;
  prefill_email: string;
  is_active: boolean;
}

const emptyForm: PackFormData = {
  name: '',
  context_text: '',
  prefill_name: '',
  prefill_email: '',
  is_active: true,
};

function packToForm(p: ContextPack): PackFormData {
  return {
    name: p.name,
    context_text: p.context_text ?? '',
    prefill_name: p.prefill_name ?? '',
    prefill_email: p.prefill_email ?? '',
    is_active: p.is_active,
  };
}

function formToCreate(f: PackFormData): ContextPackCreate {
  return {
    name: f.name.trim(),
    context_text: f.context_text.trim() || null,
    prefill_name: f.prefill_name.trim() || null,
    prefill_email: f.prefill_email.trim() || null,
    prefill_phone: null,
    is_active: f.is_active,
  };
}

function formToUpdate(f: PackFormData): ContextPackUpdate {
  return formToCreate(f);
}

function FieldRequirement({ required = false }: { required?: boolean }) {
  return (
    <span className="ml-1 text-xs font-normal text-muted-foreground">
      {required ? 'Required' : 'Optional'}
    </span>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────

export default function ContextPacksPage() {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id;
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Fetch list
  const {
    data: packsResponse,
    isLoading,
  } = useQuery({
    queryKey: ['context-packs', orgId],
    queryFn: () => contextPacksService.list(orgId!),
    enabled: !!orgId,
  });
  const packs: ContextPack[] = packsResponse?.data ?? [];

  // Fetch sales agent to get demo_slug for shareable URLs
  const { data: agentResponse } = useQuery({
    queryKey: ['sales-agent', orgId],
    queryFn: () => salesAgentService.getSalesAgent(orgId!),
    enabled: !!orgId,
  });
  const demoSlug = agentResponse?.data?.demo_slug;

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPack, setEditingPack] = useState<ContextPack | null>(null);
  const [form, setForm] = useState<PackFormData>(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState<ContextPack | null>(null);

  const openCreate = () => {
    setEditingPack(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (pack: ContextPack) => {
    setEditingPack(pack);
    setForm(packToForm(pack));
    setDialogOpen(true);
  };

  // Mutations
  const createMut = useMutation({
    mutationFn: (data: ContextPackCreate) => contextPacksService.create(orgId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['context-packs', orgId] });
      setDialogOpen(false);
      toast({ title: 'Context pack created' });
    },
    onError: () => toast({ title: 'Failed to create context pack', variant: 'destructive' }),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: ContextPackUpdate }) =>
      contextPacksService.update(orgId!, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['context-packs', orgId] });
      setDialogOpen(false);
      toast({ title: 'Context pack updated' });
    },
    onError: () => toast({ title: 'Failed to update context pack', variant: 'destructive' }),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => contextPacksService.remove(orgId!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['context-packs', orgId] });
      setDeleteTarget(null);
      toast({ title: 'Context pack deleted' });
    },
    onError: () => toast({ title: 'Failed to delete context pack', variant: 'destructive' }),
  });

  const handleSave = () => {
    if (!form.name.trim()) return;
    if (editingPack) {
      updateMut.mutate({ id: editingPack.id, data: formToUpdate(form) });
    } else {
      createMut.mutate(formToCreate(form));
    }
  };

  const isSaving = createMut.isPending || updateMut.isPending;

  const copyUrl = (pack: ContextPack) => {
    const url = buildDemoUrl(demoSlug, pack.id);
    navigator.clipboard.writeText(url);
    toast({ title: 'Demo URL copied to clipboard' });
  };

  if (!orgId) return null;

  return (
    <div className="flex-1 space-y-6 p-6 md:p-8 max-w-5xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Context Packs</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Create personalized demo links with visitor context and pre-filled name or email.
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" /> New Context Pack
        </Button>
      </div>

      {/* List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : packs.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <PackageOpen className="h-12 w-12 text-muted-foreground/40 mb-4" />
          <h3 className="text-lg font-medium">No context packs yet</h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-sm">
            Context packs let you create personalized demo links that pre-fill visitor information and inject extra context into the agent.
          </p>
          <Button className="mt-4" onClick={openCreate}>
            <Plus className="mr-2 h-4 w-4" /> Create your first
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {packs.map((pack) => (
            <div
              key={pack.id}
              className="flex items-center justify-between rounded-lg border p-4 hover:bg-accent/50 transition-colors"
            >
              <div className="space-y-1 min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium truncate">{pack.name}</span>
                  {!pack.is_active && (
                    <Badge variant="secondary" className="text-xs">Inactive</Badge>
                  )}
                </div>
                {pack.context_text && (
                  <p className="text-xs text-muted-foreground truncate max-w-lg">
                    {pack.context_text}
                  </p>
                )}
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  {pack.prefill_name && <span>Name: {pack.prefill_name}</span>}
                  {pack.prefill_email && <span>Email: {pack.prefill_email}</span>}
                </div>
              </div>
              <div className="flex items-center gap-1 ml-4 shrink-0">
                <Button variant="ghost" size="icon" onClick={() => copyUrl(pack)} title="Copy demo URL">
                  <Link className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => openEdit(pack)} title="Edit">
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(pack)} title="Delete">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[calc(100vh_-_2rem)] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPack ? 'Edit Context Pack' : 'New Context Pack'}</DialogTitle>
            <DialogDescription>
              {editingPack
                ? 'Update the context pack details.'
                : 'Create a personalized demo link with visitor context and pre-filled name or email.'}
            </DialogDescription>
          </DialogHeader>

          <div className="min-w-0 space-y-4 py-2">
            <div className="min-w-0 space-y-2">
              <Label htmlFor="cp-name">
                Name
                <FieldRequirement required />
              </Label>
              <Input
                id="cp-name"
                placeholder="e.g. Acme Corp Demo"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>

            <div className="min-w-0 space-y-2">
              <Label htmlFor="cp-context">
                Context
                <FieldRequirement />
              </Label>
              <Textarea
                id="cp-context"
                placeholder="Any extra context the agent should know about this visitor…"
                rows={4}
                value={form.context_text}
                onChange={(e) => setForm({ ...form, context_text: e.target.value })}
              />
            </div>

            <div className="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="min-w-0 space-y-2">
                <Label htmlFor="cp-prefill-name">
                  Visitor Name
                  <FieldRequirement />
                </Label>
                <Input
                  id="cp-prefill-name"
                  placeholder="Jane Doe"
                  value={form.prefill_name}
                  onChange={(e) => setForm({ ...form, prefill_name: e.target.value })}
                />
              </div>
              <div className="min-w-0 space-y-2">
                <Label htmlFor="cp-prefill-email">
                  Visitor email
                  <FieldRequirement />
                </Label>
                <Input
                  id="cp-prefill-email"
                  placeholder="jane@acme.com"
                  value={form.prefill_email}
                  onChange={(e) => setForm({ ...form, prefill_email: e.target.value })}
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Switch
                id="cp-active"
                checked={form.is_active}
                onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
              />
              <Label htmlFor="cp-active">Active</Label>
            </div>

            {/* Shareable URL preview (only for existing packs) */}
            {editingPack && (
              <div className="min-w-0 space-y-1">
                <Label className="text-xs text-muted-foreground">Demo URL</Label>
                <div className="flex min-w-0 items-center gap-2">
                  <code className="block min-w-0 flex-1 truncate rounded-md bg-muted px-3 py-2 text-xs">
                    {buildDemoUrl(demoSlug, editingPack.id)}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    className="shrink-0"
                    onClick={() => copyUrl(editingPack)}
                  >
                    <Copy className="h-3 w-3 mr-1" /> Copy
                  </Button>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={!form.name.trim() || isSaving}>
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editingPack ? 'Save Changes' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Context Pack</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &ldquo;{deleteTarget?.name}&rdquo;? Existing links using this pack will stop working.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => deleteTarget && deleteMut.mutate(deleteTarget.id)}
              disabled={deleteMut.isPending}
            >
              {deleteMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
